import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useDictionaryOverrides } from './shared/dictionary/useDictionaryOverrides';
import { usePageViewTracker } from './hooks/usePageViewTracker';
import ChangePasswordPage from './pages/ChangePasswordPage';
import PrivacyConsentPage from './pages/PrivacyConsentPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RaceSchedulePage from './pages/RaceSchedulePage';
import TenantBasicPage from './pages/TenantBasicPage';
import TenantEmploymentTypePage from './pages/TenantEmploymentTypePage';
import TenantHolidayPage from './pages/TenantHolidayPage';
import DayOffTypeFormPage from './pages/DayOffTypeFormPage';
import EmploymentTypeFormPage from './pages/EmploymentTypeFormPage';
import EmploymentTypeWorkRuleFormPage from './pages/EmploymentTypeWorkRuleFormPage';
import EmploymentTypeTagFormPage from './pages/EmploymentTypeTagFormPage';
import EmploymentTypeEditLayout from './components/EmploymentTypeEditLayout';
import WorkRulesSettingsPage from './pages/WorkRulesSettingsPage';
import WorkRulesEditPage from './pages/WorkRulesEditPage';
import OrganizationPage from './pages/OrganizationPage';
import OrgSettingsStorePage from './pages/OrgSettingsStorePage';
import OrgSettingsDepartmentPage from './pages/OrgSettingsDepartmentPage';
import StoreEditLayout from './components/StoreEditLayout';
import StoreFormPage from './pages/StoreFormPage';
import StoreOrganizationFormPage from './pages/StoreOrganizationFormPage';
import DepartmentEditLayout from './components/DepartmentEditLayout';
import DepartmentFormPage from './pages/DepartmentFormPage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeFormPage from './pages/EmployeeFormPage';
import WorkRuleFormPage from './pages/WorkRuleFormPage';
import SupportFormPage from './pages/SupportFormPage';
import TagFormPage from './pages/TagFormPage';
import EmployeeEditLayout from './components/EmployeeEditLayout';
import RequiredManHoursPage from './pages/RequiredManHoursPage';
import ShiftPatternsPage from './pages/ShiftPatternsPage';
import ShiftPatternFormPage from './pages/ShiftPatternFormPage';
import TasksPage from './pages/TasksPage';
import DisplaySettingsPage from './pages/DisplaySettingsPage';
import ComingSoonPage from './pages/ComingSoonPage';
import OperationLogsPage from './pages/OperationLogsPage';
import AccountManagementPage from './pages/AccountManagementPage';
import AccountPage from './pages/AccountPage';
import ProtectedRoute from './components/ProtectedRoute';
import LayoutRoute from './components/LayoutRoute';
import { RedirectWithParams } from './components/RedirectWithParams';
import { TenantSettingsRedirect } from './components/TenantSettingsRedirect';

const AppRoutes: React.FC = () => {
  usePageViewTracker();

  return (
    <Routes>
      {/* ── 非ログイン・特殊レイアウト ─────────────── */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* 個人情報同意（初回ログイン時）: 認証必須だが Layout なし */}
      <Route
        path="/auth/privacy-consent"
        element={
          <ProtectedRoute>
            <PrivacyConsentPage />
          </ProtectedRoute>
        }
      />

      {/* パスワード変更（初回・通常共通）: 認証必須だが Layout なし */}
      <Route
        path="/auth/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />

      {/* ── 共通レイアウト配下（認証 + Layout を一括適用） ─ */}
      <Route element={<LayoutRoute />}>
        {/* ホーム */}
        <Route path="/home" element={<RaceSchedulePage />} />

        {/* 組織設定配下 */}
        <Route path="/org-settings/organization" element={<OrganizationPage />} />
        <Route path="/org-settings/store" element={<OrgSettingsStorePage />} />
        <Route path="/org-settings/store/new" element={<StoreEditLayout />}>
          <Route index element={<StoreFormPage />} />
          <Route path="organization" element={<StoreOrganizationFormPage />} />
        </Route>
        <Route path="/org-settings/store/:id" element={<StoreEditLayout />}>
          <Route path="edit" element={<StoreFormPage />} />
          <Route path="organization" element={<StoreOrganizationFormPage />} />
        </Route>
        <Route path="/org-settings/department" element={<OrgSettingsDepartmentPage />} />
        <Route path="/org-settings/department/new" element={<DepartmentEditLayout />}>
          <Route index element={<DepartmentFormPage />} />
        </Route>
        <Route path="/org-settings/department/:id" element={<DepartmentEditLayout />}>
          <Route path="edit" element={<DepartmentFormPage />} />
        </Route>

        {/* テナント配下 */}
        <Route path="/tenant/basic" element={<TenantBasicPage />} />
        <Route path="/tenant/employment-type" element={<TenantEmploymentTypePage />} />
        <Route path="/tenant/employment-type/new" element={<EmploymentTypeEditLayout />}>
          <Route index element={<EmploymentTypeFormPage />} />
          <Route path="work-rules" element={<EmploymentTypeWorkRuleFormPage />} />
          <Route path="tags" element={<EmploymentTypeTagFormPage />} />
        </Route>
        <Route path="/tenant/employment-type/:id" element={<EmploymentTypeEditLayout />}>
          <Route path="edit" element={<EmploymentTypeFormPage />} />
          <Route path="work-rules" element={<EmploymentTypeWorkRuleFormPage />} />
          <Route path="tags" element={<EmploymentTypeTagFormPage />} />
        </Route>
        <Route path="/tenant/labor-rule" element={<WorkRulesSettingsPage />} />
        <Route path="/tenant/labor-rule/:id/edit" element={<WorkRulesEditPage />} />
        <Route path="/tenant/holiday" element={<TenantHolidayPage />} />
        <Route path="/tenant/holiday/new" element={<DayOffTypeFormPage />} />
        <Route path="/tenant/holiday/:id/edit" element={<DayOffTypeFormPage />} />
        <Route path="/tenant/billing" element={<ComingSoonPage />} />

        {/* 従業員配下 */}
        <Route path="/employee/info" element={<EmployeesPage />} />
        <Route path="/employee/info/new" element={<EmployeeEditLayout />}>
          <Route index element={<EmployeeFormPage />} />
          <Route path="work-rules" element={<WorkRuleFormPage />} />
          <Route path="support" element={<SupportFormPage />} />
          <Route path="tags" element={<TagFormPage />} />
        </Route>
        <Route path="/employee/info/:id" element={<EmployeeEditLayout />}>
          <Route path="edit" element={<EmployeeFormPage />} />
          <Route path="work-rules" element={<WorkRuleFormPage />} />
          <Route path="support" element={<SupportFormPage />} />
          <Route path="tags" element={<TagFormPage />} />
        </Route>
        <Route path="/employee/account-management" element={<AccountManagementPage />} />
        <Route path="/accounts" element={<AccountPage />} />

        {/* 店舗配下 */}
        <Route path="/store/info" element={<ComingSoonPage />} />
        <Route path="/store/man-hours" element={<RequiredManHoursPage />} />
        <Route path="/store/shift-pattern" element={<ShiftPatternsPage />} />
        <Route path="/store/shift-pattern/new" element={<ShiftPatternFormPage />} />
        <Route path="/store/shift-pattern/:id/edit" element={<ShiftPatternFormPage />} />
        <Route path="/store/task" element={<TasksPage />} />
        <Route path="/store/employment-type" element={<ComingSoonPage />} />

        {/* 操作ログ */}
        <Route path="/logs/operation" element={<OperationLogsPage />} />

        {/* 表示設定配下 */}
        <Route path="/display-settings/dictionary" element={<DisplaySettingsPage />} />
        <Route path="/display-settings/monthly-shift" element={<DisplaySettingsPage />} />
        <Route path="/display-settings/daily-shift" element={<DisplaySettingsPage />} />

        {/* ── リダイレクト ── */}
        <Route path="/tenant" element={<Navigate to="/tenant/basic" replace />} />
        <Route path="/store" element={<Navigate to="/store/shift-pattern" replace />} />
        <Route path="/employee" element={<Navigate to="/employee/info" replace />} />
        <Route path="/org-settings" element={<Navigate to="/org-settings/store" replace />} />
        <Route path="/display-settings" element={<Navigate to="/display-settings/dictionary" replace />} />

        {/* 旧ルート: テナント設定・雇用形態 CRUD → 新URLへリダイレクト */}
        <Route path="/tenant-settings" element={<TenantSettingsRedirect />} />
        <Route path="/tenant-settings/employment-type/new" element={<Navigate to="/tenant/employment-type/new" replace />} />
        <Route path="/tenant-settings/employment-type/:id/edit" element={<RedirectWithParams to="/tenant/employment-type/:id/edit" />} />
        <Route path="/tenant-settings/employment-type/new/work-rules" element={<Navigate to="/tenant/employment-type/new/work-rules" replace />} />
        <Route path="/tenant-settings/employment-type/:id/work-rules" element={<RedirectWithParams to="/tenant/employment-type/:id/work-rules" />} />
        <Route path="/tenant-settings/employment-type/new/tags" element={<Navigate to="/tenant/employment-type/new/tags" replace />} />
        <Route path="/tenant-settings/employment-type/:id/tags" element={<RedirectWithParams to="/tenant/employment-type/:id/tags" />} />
        <Route path="/organization" element={<Navigate to="/org-settings/organization" replace />} />
        <Route path="/tenant/organization" element={<Navigate to="/org-settings/organization" replace />} />
        <Route path="/tenant/store-setting" element={<Navigate to="/org-settings/store" replace />} />
        <Route path="/tenant/department" element={<Navigate to="/org-settings/department" replace />} />
        <Route path="/work-rules" element={<Navigate to="/tenant/labor-rule" replace />} />

        {/* 旧ルート: 従業員 → 新URLへリダイレクト */}
        <Route path="/employees" element={<Navigate to="/employee/info" replace />} />
        <Route path="/employees/new" element={<Navigate to="/employee/info/new" replace />} />
        <Route path="/employees/:id/edit" element={<RedirectWithParams to="/employee/info/:id/edit" />} />
        <Route path="/employees/new/work-rules" element={<Navigate to="/employee/info/new/work-rules" replace />} />
        <Route path="/employees/:id/work-rules" element={<RedirectWithParams to="/employee/info/:id/work-rules" />} />
        <Route path="/employees/new/support" element={<Navigate to="/employee/info/new/support" replace />} />
        <Route path="/employees/:id/support" element={<RedirectWithParams to="/employee/info/:id/support" />} />
        <Route path="/employees/new/tags" element={<Navigate to="/employee/info/new/tags" replace />} />
        <Route path="/employees/:id/tags" element={<RedirectWithParams to="/employee/info/:id/tags" />} />

        {/* 旧ルート: 店舗 → 新URLへリダイレクト */}
        <Route path="/required-manhours" element={<Navigate to="/store/man-hours" replace />} />
        <Route path="/shift-patterns" element={<Navigate to="/store/shift-pattern" replace />} />
        <Route path="/shift-patterns/new" element={<Navigate to="/store/shift-pattern/new" replace />} />
        <Route path="/shift-patterns/:id/edit" element={<RedirectWithParams to="/store/shift-pattern/:id/edit" />} />
        <Route path="/tasks" element={<Navigate to="/store/task" replace />} />

        {/* 旧ルート: 本部設定・働き方ルール → 新URLへリダイレクト */}
        <Route path="/settings" element={<Navigate to="/tenant/basic" replace />} />
        <Route path="/settings/work-rules/:viewMode" element={<Navigate to="/tenant/labor-rule" replace />} />

        {/* ルートパスはホームへリダイレクト */}
        <Route path="/" element={<Navigate to="/home" replace />} />
      </Route>

      {/* 404ページ */}
      <Route
        path="*"
        element={
          <div className="min-h-svh flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
              <p className="text-gray-600 mb-8">ページが見つかりません</p>
              <a
                href="/home"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700"
              >
                ホームへ戻る
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
};

/**
 * Redis 辞書上書きを i18next にマージする。
 * useAuth() を使うため AuthProvider の内側で呼ぶ必要がある。
 */
const DictionaryLoader: React.FC = () => {
  const { user } = useAuth();
  useDictionaryOverrides(user?.tenantId);
  return null;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <DictionaryLoader />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
};

export default App;
